import {
  useDeleteCampaignAnalysisMutation,
  useRenameCampaignAnalysisMutation,
} from '@/features/campaign/hooks/mutation';
import { useCampaignAnalysisResult } from '@/features/campaign/hooks/query';
import type { CampaignAnalysisHistoryItem } from '@/entities/campaign/generated/campaign-type';
import { Button } from '@/shared/components/shadcn/button';
import { Card } from '@/shared/components/shadcn/card';
import { Skeleton } from '@/shared/components/shadcn/skeleton';
import { AlertCircle } from 'lucide-react';

import { getCampaignAnalysisRunTitle } from '../helpers/analysis-history';
import { CampaignAnalysisReport } from './report/campaign-analysis-report';
import { CampaignAnalysisActionsMenu } from './campaign-analysis-actions-menu';

type Props = {
  campaignId: string;
  onDeleted: () => void;
  onOpenDiscovery: (prompt: string) => void;
  run: CampaignAnalysisHistoryItem;
};

export function CampaignAnalysisContent({ campaignId, onDeleted, onOpenDiscovery, run }: Props) {
  const resultQuery = useCampaignAnalysisResult(campaignId, run.run_id);
  const renameAnalysis = useRenameCampaignAnalysisMutation();
  const deleteAnalysis = useDeleteCampaignAnalysisMutation();
  const title = getCampaignAnalysisRunTitle(run);

  return (
    <div aria-label={`${title} analysis content`} className="min-w-0" role="region">
      {resultQuery.isPending ? <CampaignAnalysisResultLoading /> : null}
      {resultQuery.isError ? (
        <CampaignAnalysisResultError onRetry={() => void resultQuery.refetch()} />
      ) : null}
      {resultQuery.data ? (
        <CampaignAnalysisReport
          actions={
            <CampaignAnalysisActionsMenu
              onDelete={async () => {
                await deleteAnalysis.mutateAsync({
                  campaignId,
                  runId: run.run_id,
                });
                onDeleted();
              }}
              onRename={(nextTitle) =>
                renameAnalysis.mutateAsync({
                  campaignId,
                  runId: run.run_id,
                  title: nextTitle,
                })
              }
              title={resultQuery.data.meta.title}
            />
          }
          onOpenDiscovery={onOpenDiscovery}
          report={resultQuery.data}
        />
      ) : null}
    </div>
  );
}

function CampaignAnalysisResultLoading() {
  return (
    <Card
      aria-label="Loading analysis report"
      className="border-border bg-card min-h-105 space-y-8 p-8"
      role="status"
    >
      <div className="space-y-3">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-10 w-3/5" />
      </div>
      <Skeleton className="h-28 w-full" />
      <Skeleton className="h-64 w-full" />
    </Card>
  );
}

function CampaignAnalysisResultError({ onRetry }: { onRetry: () => void }) {
  return (
    <Card className="border-border bg-card flex min-h-105 items-center justify-center p-8 text-center">
      <div className="max-w-md">
        <AlertCircle className="text-destructive mx-auto size-6" aria-hidden="true" />
        <h3 className="text-foreground mt-4 text-base font-semibold">Report unavailable</h3>
        <p className="text-muted-foreground mt-2 text-sm leading-6" role="alert">
          The completed analysis could not be loaded. Your selected run is unchanged.
        </p>
        <Button className="mt-5" onClick={onRetry} size="sm" variant="outline">
          Retry
        </Button>
      </div>
    </Card>
  );
}
